import { ref, shallowRef, type Ref, type ShallowRef } from 'vue';
import { getRuntimeConfig } from '../config/runtime';
import api from '../services/api';
import { routedFetch } from '../services/apiRouter';
import { hubAbsoluteStreamUrl } from '../services/cameraStreamUrls';
import { DEFAULT_STUN_ICE_SERVERS } from '../services/webrtcConfig';
import {
  ICE_GATHER_WINDOW_MS,
  MEDIA_PATH_BUDGET_MS,
  gatherIce,
  watchMediaPath,
  type IceGathering,
  type MediaPathFailureReason,
  type MediaPathWatch,
} from '../services/mediaPath';
import { tt } from '../utils/consoleText';

export type CameraStreamPreference = 'main' | 'prefer-sub';
export type CameraStreamQuality = 'main' | 'sub';

/**
 * Where this session's attempt at a live media path stands (card `t_20b35b28`).
 *
 * `connecting` and `connected` cannot express the measured symptom — a WHEP
 * answer go2rtc accepted and ICE that never completes — so before this state
 * existed the live view spun in `connecting` forever with nothing to say. The
 * four names are the whole story of one attempt:
 *
 *   `idle`           nothing has been attempted (or the last attempt was torn down)
 *   `connecting`     the offer is out; ICE has not completed
 *   `live`           `connectionState === 'connected'` — media can flow
 *   `no-media-path`  the attempt ended without one: the budget expired
 *                    (`services/mediaPath.ts`), ICE failed, or nothing was
 *                    gathered to offer. The surface must *say* this, never spin.
 */
export type MediaPathState = 'idle' | 'connecting' | 'live' | 'no-media-path';

/** The facts behind a `MediaPathState` — what the attempt actually waited for. */
export interface MediaPathDetail {
  /** Why the state is what it is. `budget` is the measured forever-`checking` case. */
  reason: 'connected' | MediaPathFailureReason;
  /** The ICE gathering window's own census, when an offer was built. */
  gathering: IceGathering | null;
  /** How long the wait for `connected` ran, ms (`null` when nothing was posted). */
  waitedMs: number | null;
}

interface CameraEndpoints {
  whepUrl: string;
  whepSubUrl: string | null;
  iceServers: RTCIceServer[];
}

interface EndpointCacheEntry {
  expiresAt: number;
  promise: Promise<CameraEndpoints>;
}

export interface CameraStreamSession {
  cameraId: string;
  quality: CameraStreamQuality;
  connecting: Ref<boolean>;
  connected: Ref<boolean>;
  error: Ref<string | null>;
  /** Whether this session has a live media path, and if not, what happened (card `t_20b35b28`). */
  mediaPath: Ref<MediaPathState>;
  mediaPathDetail: Ref<MediaPathDetail | null>;
  hasAudio: Ref<boolean>;
  stream: ShallowRef<MediaStream | null>;
  connect: () => Promise<boolean>;
  disconnect: (force?: boolean) => void;
  reconnect: () => Promise<boolean>;
  attachVideoElement: (element: HTMLVideoElement) => void;
  detachVideoElement: (element: HTMLVideoElement) => void;
}

const DEFAULT_ICE_SERVERS: RTCIceServer[] = DEFAULT_STUN_ICE_SERVERS;
const ENDPOINT_CACHE_TTL_MS = 5_000;
const DEFAULT_RETAIN_MS = 1_500;

const endpointCache = new Map<string, EndpointCacheEntry>();
const sessionCache = new Map<string, CameraStreamSession>();

function sessionKey(cameraId: string, quality: CameraStreamQuality): string {
  return `${cameraId}:${quality}`;
}

async function fetchCameraEndpoints(cameraId: string, refresh = false): Promise<CameraEndpoints> {
  const now = Date.now();
  const cached = endpointCache.get(cameraId);
  if (!refresh && cached && cached.expiresAt > now) {
    return cached.promise;
  }

  const promise = (async () => {
    const response = await api.get(`/cameras/${cameraId}/go2rtc/endpoints`);
    const data = response.data || response;

    if (!data.registered || !data.endpoints?.whepUrl) {
      throw new Error(tt('Camera not registered with go2rtc', 'Kamera ni registrirana v go2rtc'));
    }

    // Backend may still return absolute http:// URLs behind TLS termination.
    // Normalize to same-origin HTTPS (or relative) so browser WHEP POSTs don't
    // hit mixed-content / CORS preflight redirect failures.
    const normalizeWhepUrl = (url: string | null | undefined): string | null => {
      if (!url) return null;
      // The hub answers a same-origin path for the WHEP endpoint, which is only
      // usable as one when THIS page is the hub. The desktop app (`file://`) and
      // the phone resolve `/api/…` against their own origin, so the offer never
      // leaves the app: `services/hubRequestRouting` reads the path as cloud-bound
      // and `routedFetch` refuses it (no WebRTC channel on a LAN hub), and a bare
      // `fetch` throws `TypeError: Failed to fetch`. Cameras then never play while
      // snapshots (absolute URLs) are fine. Resolve against the hub's own base
      // first — `otto-local://<host>` in Electron, a LAN address on the phone;
      // cloud bases keep the path for the channel, so they are left alone.
      const target = hubAbsoluteStreamUrl(url, getRuntimeConfig().apiUrl);
      if (target.startsWith('/')) return target;
      if (typeof window === 'undefined') return target;
      try {
        const parsed = new URL(target, window.location.origin);
        if (window.location.protocol === 'https:' && parsed.protocol === 'http:') {
          // Prefer relative same-origin path when host matches
          if (parsed.host === window.location.host) {
            return `${parsed.pathname}${parsed.search}`;
          }
          parsed.protocol = 'https:';
          return parsed.toString();
        }
        return parsed.toString();
      } catch {
        return target;
      }
    };

    const whepUrl = normalizeWhepUrl(data.endpoints.whepUrl as string);
    if (!whepUrl) {
      throw new Error(tt('Camera not registered with go2rtc', 'Kamera ni registrirana v go2rtc'));
    }

    return {
      whepUrl,
      whepSubUrl: normalizeWhepUrl((data.endpoints.whepSubUrl as string | undefined) || null),
      // An **empty array is truthy** in JS, so `|| DEFAULT_ICE_SERVERS` handed the
      // peer connection *no* ICE servers whenever the hub answered `[]` — a
      // relay-less offer and the forever-`checking` symptom (measured on card
      // `t_c1687051`, handed to `t_4623769a`). `[]` means "the hub has none to
      // give", which is what the STUN fallback is for.
      iceServers:
        (data.endpoints.iceServers as RTCIceServer[] | undefined)?.length
          ? (data.endpoints.iceServers as RTCIceServer[])
          : DEFAULT_ICE_SERVERS,
    } satisfies CameraEndpoints;
  })();

  endpointCache.set(cameraId, {
    expiresAt: now + ENDPOINT_CACHE_TTL_MS,
    promise,
  });

  try {
    return await promise;
  } catch (error) {
    endpointCache.delete(cameraId);
    throw error;
  }
}

/**
 * Endpoints with a short retry window. Right after a hub restart go2rtc is
 * briefly without registered streams (boot re-registration takes a while), so
 * live view / intercom calls retry instead of failing with "Camera not
 * registered with go2rtc".
 */
export async function resolveCameraEndpoints(cameraId: string): Promise<CameraEndpoints> {
  const delays = [0, 1200, 3000];
  let lastError: unknown;
  for (const delay of delays) {
    if (delay) {
      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });
    }
    try {
      return await fetchCameraEndpoints(cameraId, delay > 0);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error(tt('Camera not registered with go2rtc', 'Kamera ni registrirana v go2rtc'));
}

function resolveQuality(
  endpoints: CameraEndpoints,
  preference: CameraStreamPreference,
): { quality: CameraStreamQuality; whepUrl: string; iceServers: RTCIceServer[] } {
  if (preference === 'prefer-sub' && endpoints.whepSubUrl) {
    return {
      quality: 'sub',
      whepUrl: endpoints.whepSubUrl,
      iceServers: endpoints.iceServers,
    };
  }

  return {
    quality: 'main',
    whepUrl: endpoints.whepUrl,
    iceServers: endpoints.iceServers,
  };
}

function createSession(
  cameraId: string,
  quality: CameraStreamQuality,
  whepUrl: string,
  iceServers: RTCIceServer[],
): CameraStreamSession {
  const connecting = ref(false);
  const connected = ref(false);
  const error = ref<string | null>(null);
  const mediaPath = ref<MediaPathState>('idle');
  const mediaPathDetail = ref<MediaPathDetail | null>(null);
  const hasAudio = ref(false);
  const stream = shallowRef<MediaStream | null>(null);
  const peerConnection = shallowRef<RTCPeerConnection | null>(null);

  const attachedElements = new Set<HTMLVideoElement>();
  let retainTimer: ReturnType<typeof setTimeout> | null = null;
  let connectPromise: Promise<boolean> | null = null;
  let mediaPathWatch: MediaPathWatch | null = null;
  /** What the last gathering window saw — carried into a failure's detail. */
  let lastGathering: IceGathering | null = null;

  function clearRetainTimer() {
    if (!retainTimer) return;
    clearTimeout(retainTimer);
    retainTimer = null;
  }

  function applyStreamToElement(element: HTMLVideoElement) {
    element.autoplay = true;
    element.playsInline = true;

    if (element.srcObject !== stream.value) {
      element.srcObject = stream.value;
    }
  }

  function clearElement(element: HTMLVideoElement) {
    try {
      element.pause();
    } catch {
      // Ignore pause errors during teardown.
    }
    element.srcObject = null;
  }

  function syncAttachedElements() {
    attachedElements.forEach((element) => {
      if (stream.value) {
        applyStreamToElement(element);
      } else {
        clearElement(element);
      }
    });
  }

  function closeConnection(resetError = true) {
    clearRetainTimer();
    // A teardown is not a verdict: stop the watcher before the connection it
    // watches closes, or the close would be reported back as a failure.
    mediaPathWatch?.cancel();
    mediaPathWatch = null;
    peerConnection.value?.close();
    peerConnection.value = null;
    connectPromise = null;
    connecting.value = false;
    connected.value = false;
    hasAudio.value = false;
    stream.value = null;
    mediaPath.value = 'idle';
    mediaPathDetail.value = null;
    if (resetError) {
      error.value = null;
    }
    syncAttachedElements();
  }

  /**
   * End an attempt that produced no media path: release the connection and
   * install the named state — *after* `closeConnection`, which resets it, so the
   * verdict outlives the connection it is about (card `t_20b35b28`).
   */
  function endWithoutMediaPath(detail: MediaPathDetail) {
    closeConnection(false);
    mediaPath.value = 'no-media-path';
    mediaPathDetail.value = detail;
  }

  function scheduleDisconnect(force = false) {
    clearRetainTimer();

    if (force || attachedElements.size > 0) {
      closeConnection(true);
      return;
    }

    retainTimer = setTimeout(() => {
      retainTimer = null;
      if (attachedElements.size === 0) {
        closeConnection(true);
      }
    }, DEFAULT_RETAIN_MS);
  }

  async function connect(): Promise<boolean> {
    clearRetainTimer();

    if (connected.value && peerConnection.value) {
      return true;
    }

    if (connectPromise) {
      return connectPromise;
    }

    connectPromise = (async () => {
      connecting.value = true;
      error.value = null;
      mediaPath.value = 'connecting';
      mediaPathDetail.value = null;

      try {
        if (peerConnection.value) {
          peerConnection.value.close();
          peerConnection.value = null;
        }

        const pc = new RTCPeerConnection({ iceServers, iceCandidatePoolSize: 10 });
        peerConnection.value = pc;

        pc.addTransceiver('video', { direction: 'recvonly' });
        pc.addTransceiver('audio', { direction: 'recvonly' });

        pc.ontrack = (event) => {
          const nextStream = event.streams[0];
          if (!nextStream) return;

          stream.value = nextStream;
          hasAudio.value = nextStream.getAudioTracks().length > 0;
          syncAttachedElements();
        };

        pc.onconnectionstatechange = () => {
          switch (pc.connectionState) {
            case 'connected':
              connected.value = true;
              connecting.value = false;
              error.value = null;
              mediaPath.value = 'live';
              mediaPathDetail.value = { reason: 'connected', gathering: lastGathering, waitedMs: null };
              break;
            case 'disconnected':
            case 'closed':
              connected.value = false;
              connecting.value = false;
              break;
            case 'failed':
              connected.value = false;
              connecting.value = false;
              error.value = tt('Connection failed', 'Povezava ni uspela');
              // ICE itself gave up: the same named state as a spent budget, and
              // the surface's own sentence (the Red-box string above stays for
              // the failures that are not about a media path at all).
              mediaPath.value = 'no-media-path';
              mediaPathDetail.value = { reason: 'failed', gathering: lastGathering, waitedMs: null };
              break;
          }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        // The gathering window, and the census it produced (card `t_20b35b28`
        // §111): the app does not trickle, so the offer carries whatever was
        // gathered when this window closes. A window that closes while gathering
        // is still open can silently produce a relay-less offer — which fails
        // exactly like the forever-`checking` symptom this card is about.
        const gathering = await gatherIce(pc);
        lastGathering = gathering;

        if (!gathering.complete) {
          console.warn(
            `[CameraStreamSession] ICE gathering was still open when the ${ICE_GATHER_WINDOW_MS} ms window closed — this offer may carry no relay candidate, and a slow TURN allocation looks exactly like a media path that never comes up`,
            { cameraId, quality, gathering },
          );
        }

        if (gathering.candidates.total === 0) {
          // Nothing was gathered at all: no candidate pair can ever be checked,
          // so this offer cannot reach the camera and nothing is posted. Said
          // loudly, and said *now*, instead of a silent spinner for the budget.
          console.warn('[CameraStreamSession] ICE gathered no candidates at all — the offer cannot reach this camera', {
            cameraId,
            quality,
            gathering,
          });
          endWithoutMediaPath({ reason: 'no-candidates', gathering, waitedMs: null });
          return false;
        }

        // In Electron cloud mode the WHEP URL points to the hub's local
        // API (e.g. http://localhost:16161/api/...) which is unreachable
        // directly.  Route via routedFetch so it travels over the WebRTC
        // data channel in cloud mode and falls through to direct HTTP locally.
        const whepResponse = await routedFetch(whepUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/sdp' },
          body: pc.localDescription?.sdp,
        });

        if (!whepResponse.ok) {
          const errorText = (await whepResponse.text().catch(() => '')).trim();
          throw new Error(
            errorText
              ? tt(
                  `WHEP request failed: ${whepResponse.status} - ${errorText}`,
                  `Zahteva WHEP ni uspela: ${whepResponse.status} - ${errorText}`,
                )
              : tt(`WHEP request failed: ${whepResponse.status}`, `Zahteva WHEP ni uspela: ${whepResponse.status}`),
          );
        }

        const answerSdp = await whepResponse.text();
        await pc.setRemoteDescription(
          new RTCSessionDescription({
            type: 'answer',
            sdp: answerSdp,
          }),
        );

        // The answer is in. Now the question this card exists for: does a media
        // path actually come up? Bounded (`services/mediaPath.ts`), because the
        // measured failure is an ICE that never completes and never errors —
        // the live view spun in `connecting` forever with nothing to say.
        const watch = watchMediaPath(pc);
        mediaPathWatch = watch;
        const waitStarted = Date.now();
        const outcome = await watch.outcome;
        mediaPathWatch = null;

        // Somebody tore this session down while we were waiting: not our verdict.
        if (outcome === 'closed') {
          return false;
        }

        if (outcome !== 'live') {
          const waitedMs = Date.now() - waitStarted;
          console.warn(
            `[CameraStreamSession] No media path ${outcome === 'failed' ? '— ICE gave up' : `within the ${MEDIA_PATH_BUDGET_MS} ms budget — the camera took the offer and ICE never completed`}`,
            { cameraId, quality, waitedMs, budgetMs: MEDIA_PATH_BUDGET_MS, gathering },
          );
          endWithoutMediaPath({
            reason: outcome === 'failed' ? 'failed' : 'budget',
            gathering,
            waitedMs,
          });
          return false;
        }

        return true;
      } catch (__caught: unknown) {
        const err = __caught as Error;
        console.error('[CameraStreamSession] Connection failed:', {
          cameraId,
          quality,
          error: err,
        });

        error.value = err?.message || tt('Connection failed', 'Povezava ni uspela');
        closeConnection(false);
        return false;
      } finally {
        connectPromise = null;
      }
    })();

    return connectPromise;
  }

  function disconnect(force = false) {
    scheduleDisconnect(force);
  }

  async function reconnect(): Promise<boolean> {
    closeConnection(true);
    return connect();
  }

  function attachVideoElement(element: HTMLVideoElement) {
    clearRetainTimer();
    attachedElements.add(element);

    if (stream.value) {
      applyStreamToElement(element);
    }
  }

  function detachVideoElement(element: HTMLVideoElement) {
    if (!attachedElements.has(element)) return;

    attachedElements.delete(element);
    clearElement(element);

    if (attachedElements.size === 0) {
      scheduleDisconnect(false);
    }
  }

  return {
    cameraId,
    quality,
    connecting,
    connected,
    error,
    mediaPath,
    mediaPathDetail,
    hasAudio,
    stream,
    connect,
    disconnect,
    reconnect,
    attachVideoElement,
    detachVideoElement,
  };
}

export async function resolveCameraStreamSession(
  cameraId: string,
  preference: CameraStreamPreference = 'main',
): Promise<CameraStreamSession> {
  const endpoints = await resolveCameraEndpoints(cameraId);
  const target = resolveQuality(endpoints, preference);
  const key = sessionKey(cameraId, target.quality);

  let session = sessionCache.get(key);
  if (!session) {
    session = createSession(cameraId, target.quality, target.whepUrl, target.iceServers);
    sessionCache.set(key, session);
  }

  return session;
}

export function invalidateCameraStreamEndpoints(cameraId: string) {
  endpointCache.delete(cameraId);
}
